'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Bell, Mail, Smartphone, Calendar, Search, Loader2, ChevronDown } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import Icon from '@/components/ui/AppIcon';


interface AddAlertForm {
  blNumber: string;
  carrier: string;
  email: string;
  mobile: string;
  alertDate: string;
  notifyEmail: boolean;
  notifySMS: boolean;
  notifyPush: boolean;
}

interface Alert {
  id: string;
  blNumber: string;
  carrier: string;
  currentStatus: string;
  origin: string;
  destination: string;
  eta: string;
  alertDate: string;
  channels: string[];
  lastTriggered: string | null;
  createdAt: string;
  active: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onAdded: (alert: Alert) => void;
}

const CARRIERS = [
  'Maersk', 'MSC', 'CMA CGM', 'Hapag-Lloyd', 'ONE Line',
  'Evergreen', 'COSCO', 'Yang Ming', 'HMM', 'ZIM', 'OOCL',
  'PIL', 'SCI India', 'Shreyas Shipping', 'Great Eastern',
];

export default function AddAlertModal({ open, onClose, onAdded }: Props) {
  const [step, setStep] = useState<1 | 2>(1);
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AddAlertForm>({
    defaultValues: { notifyEmail: true, notifySMS: false, notifyPush: false },
  });

  const blNumber = watch('blNumber');

  const onSubmit = async (data: AddAlertForm) => {
    // Backend integration point: POST /api/alerts with BL details and notification preferences
    await new Promise((r) => setTimeout(r, 1100));
    const channels: string[] = [];
    if (data.notifyEmail) channels.push('Email');
    if (data.notifySMS) channels.push('SMS');
    if (data.notifyPush) channels.push('Push');

    const newAlert: Alert = {
      id: `alert-new-${Date.now()}`,
      blNumber: data.blNumber.toUpperCase().trim(),
      carrier: data.carrier,
      currentStatus: 'Booking Confirmed',
      origin: 'JNPT (Nhava Sheva)',
      destination: 'TBC',
      eta: 'TBC',
      alertDate: data.alertDate,
      channels,
      lastTriggered: null,
      createdAt: '06/09/2026',
      active: true,
    };
    onAdded(newAlert);
    onClose();
    reset();
    setStep(1);
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => { reset(); setStep(1); }, 300);
  };

  return (
    <Modal open={open} onClose={handleClose} title="Add New BL Alert" size="md">
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6">
        {[1, 2].map((s) => (
          <React.Fragment key={`step-${s}`}>
            <div className={`flex items-center gap-2 ${s <= step ? 'text-primary' : 'text-muted-foreground'}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                s < step ? 'bg-success text-white' : s === step ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
              }`}>
                {s}
              </div>
              <span className="text-xs font-semibold hidden sm:block">
                {s === 1 ? 'BL Details' : 'Notification Setup'}
              </span>
            </div>
            {s < 2 && <div className={`flex-1 h-px ${step > s ? 'bg-success' : 'bg-border'}`} />}
          </React.Fragment>
        ))}
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {step === 1 && (
          <div className="flex flex-col gap-4 fade-in">
            {/* BL Number */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">
                Bill of Lading Number <span className="text-danger">*</span>
              </label>
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  placeholder="e.g. MAEU123456789"
                  {...register('blNumber', {
                    required: 'BL number is required.',
                    minLength: { value: 8, message: 'BL number must be at least 8 characters.' },
                  })}
                  className="input-field w-full h-11 pl-9 pr-4 text-sm uppercase"
                />
              </div>
              {errors.blNumber && <p className="text-xs text-danger mt-1">{errors.blNumber.message}</p>}
            </div>

            {/* Carrier */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">
                Carrier <span className="text-danger">*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">Select the shipping line for this BL</p>
              <div className="relative">
                <ChevronDown size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <select
                  {...register('carrier', { required: 'Please select a carrier.' })}
                  className="input-field w-full h-11 pl-4 pr-9 text-sm appearance-none cursor-pointer"
                >
                  <option value="">Select carrier...</option>
                  {CARRIERS.map((c) => (
                    <option key={`add-carrier-${c}`} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              {errors.carrier && <p className="text-xs text-danger mt-1">{errors.carrier.message}</p>}
            </div>

            <button
              type="button"
              disabled={!blNumber || blNumber.trim().length < 8}
              onClick={() => setStep(2)}
              className="btn-primary h-11 flex items-center justify-center gap-2 text-sm mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue to Notifications
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="flex flex-col gap-4 fade-in">
            {/* Alert date */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">
                Alert Date <span className="text-danger">*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">
                Receive a reminder on this date about shipment status
              </p>
              <div className="relative">
                <Calendar size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input
                  type="date"
                  {...register('alertDate', { required: 'Alert date is required.' })}
                  className="input-field w-full h-11 pl-9 pr-4 text-sm"
                />
              </div>
              {errors.alertDate && <p className="text-xs text-danger mt-1">{errors.alertDate.message}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">
                Email Address <span className="text-danger">*</span>
              </label>
              <div className="relative">
                <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input
                  type="email"
                  placeholder="yourname@company.com"
                  {...register('email', {
                    required: 'Email is required.',
                    pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email.' },
                  })}
                  className="input-field w-full h-11 pl-9 pr-4 text-sm"
                />
              </div>
              {errors.email && <p className="text-xs text-danger mt-1">{errors.email.message}</p>}
            </div>

            {/* Mobile */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">
                Mobile Number <span className="text-muted-foreground text-xs font-normal">(Optional)</span>
              </label>
              <div className="flex gap-2">
                <div className="h-11 px-3 flex items-center text-sm font-semibold text-foreground bg-muted border border-border rounded-lg shrink-0">
                  +91
                </div>
                <div className="relative flex-1">
                  <Smartphone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <input
                    type="tel"
                    placeholder="98765 43210"
                    {...register('mobile', {
                      pattern: { value: /^[6-9]\d{9}$/, message: 'Enter a valid 10-digit Indian mobile number.' },
                    })}
                    className="input-field w-full h-11 pl-9 pr-4 text-sm"
                  />
                </div>
              </div>
              {errors.mobile && <p className="text-xs text-danger mt-1">{errors.mobile.message}</p>}
            </div>

            {/* Channels */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                Notification Channels
              </label>
              <div className="flex flex-col gap-2">
                {[
                  { name: 'notifyEmail' as const, label: 'Email Notifications', icon: Mail },
                  { name: 'notifySMS' as const, label: 'SMS Notifications', icon: Smartphone },
                  { name: 'notifyPush' as const, label: 'Push Notifications (Browser)', icon: Bell },
                ].map((ch) => {
                  const Icon = ch.icon;
                  return (
                    <label
                      key={`addch-${ch.name}`}
                      className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        {...register(ch.name)}
                        className="w-4 h-4 accent-primary rounded"
                      />
                      <Icon size={15} className="text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">{ch.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-3 mt-1">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex-1 h-11 text-sm font-semibold text-muted-foreground border border-border rounded-lg hover:bg-muted transition-colors"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary flex-1 h-11 flex items-center justify-center gap-2 text-sm"
              >
                {isSubmitting ? (
                  <><Loader2 size={16} className="animate-spin" /> Adding Alert...</>
                ) : (
                  <><Bell size={15} /> Add Alert</>
                )}
              </button>
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
}